import { PERMISSIONS } from '@ibe/constants';
import { Button, StatusBadge } from '@ibe/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ImportDialog } from '../../../components/import-dialog/import-dialog.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { PermissionGuard } from '../../../auth/permission-guard.js';
import { countryRowSchema } from '../api/schemas.js';

/**
 * The countries screen.
 *
 * A descriptor rather than a page, like every other list in the portal: what
 * varies is the columns, the endpoint and the permission, and what does not
 * vary is in `ResourceListPage`.
 */
export function CountryListPage(): React.JSX.Element {
  const [importing, setImporting] = useState(false);

  return (
    <>
      <ResourceListPage
        title="Countries"
        description="ISO 3166-1 countries. Search by name or code."
        path="/v1/countries"
        resource="countries"
        schema={countryRowSchema}
        permission={PERMISSIONS.REFERENCE_READ}
        rowKey={(row) => row.id}
        defaultSort={{ columnId: 'name', direction: 'asc' }}
        actions={
          <PermissionGuard permission={PERMISSIONS.REFERENCE_IMPORT}>
            <Button
              onClick={() => {
                setImporting(true);
              }}
            >
              Import
            </Button>
          </PermissionGuard>
        }
        columns={[
          {
            id: 'code',
            header: 'Code',
            sortable: true,
            fields: ['code'],
            cell: (row) => (
              <Link
                className="font-medium underline-offset-4 hover:underline"
                to={`/countries/${row.id}`}
              >
                {row.code}
              </Link>
            ),
          },
          { id: 'name', header: 'Name', sortable: true, fields: ['name'], cell: (row) => row.name },
          { id: 'alpha3', header: 'Alpha-3', fields: ['alpha3'], cell: (row) => row.alpha3 ?? '—' },
          {
            id: 'phonePrefix',
            header: 'Dialling code',
            fields: ['phonePrefix'],
            cell: (row) => row.phonePrefix ?? '—',
          },
          {
            id: 'isActive',
            header: 'Status',
            fields: ['isActive'],
            cell: (row) => (
              <StatusBadge tone={row.isActive === true ? 'success' : 'neutral'}>
                {row.isActive === true ? 'Active' : 'Inactive'}
              </StatusBadge>
            ),
          },
        ]}
      />

      <ImportDialog
        resource="countries"
        basePath="/v1/countries"
        open={importing}
        onClose={() => {
          setImporting(false);
        }}
      />
    </>
  );
}
